import {
    type ReactNode,
    useState
} from "react";
import {
    StyleSheet,
    View
} from "react-native";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    formatCurrency,
    formatDateTime,
    formatNumber,
    PieChart,
    Chart
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import type {
    ChartConfigItem,
    ChartDataRow,
    ChartConfig
} from "ncore-ui-kit";
import {
    BulletList,
    Paragraph,
    CodeBlock,
    Article,
    Preview,
    Section
} from "../../components/content";

const stylesheet = StyleSheet.create({
    chart: {
        width: "100%"
    }
});

const NAME = "PieChart";

const USAGE_CODE = `<Chart
    config={{
        chrome: {
            title: "Chrome",
            colorIndex: 1
        },
        safari: {
            title: "Safari",
            colorIndex: 2
        },
        edge: {
            title: "Edge",
            colorIndex: 3
        }
    }}
    description="Today's visitors, total in the centre"
    title="Browsers"
>
    <PieChart
        categoryKey="browser"
        valueKey="visitors"
        data={data}
    />
</Chart>`;

const BROWSERS = [
    {
        title: "Chrome",
        key: "chrome",
        visitors: 742
    },
    {
        title: "Safari",
        visitors: 214,
        key: "safari"
    },
    {
        title: "Samsung Internet",
        key: "samsung",
        visitors: 72
    },
    {
        title: "Edge",
        visitors: 56,
        key: "edge"
    },
    {
        title: "Firefox",
        key: "firefox",
        visitors: 41
    }
] as const;

const DEVICES = [
    {
        titleKey: "pieChartPage.deviceAndroid",
        key: "android",
        value: 18420
    },
    {
        titleKey: "pieChartPage.deviceIphone",
        key: "iphone",
        value: 9860
    },
    {
        titleKey: "pieChartPage.deviceDesktop",
        key: "desktop",
        value: 7240
    },
    {
        titleKey: "pieChartPage.deviceTablet",
        key: "tablet",
        value: 1180
    }
] as const;

const TICKETS = [
    {
        titleKey: "pieChartPage.ticketShipping",
        key: "shipping",
        value: 267
    },
    {
        titleKey: "pieChartPage.ticketReturns",
        key: "returns",
        value: 178
    },
    {
        titleKey: "pieChartPage.ticketPayment",
        key: "payment",
        value: 103
    },
    {
        titleKey: "pieChartPage.ticketMembership",
        key: "membership",
        value: 64
    },
    {
        titleKey: "pieChartPage.ticketProduct",
        key: "product",
        value: 28
    }
] as const;

const SALES = [
    {
        titleKey: "pieChartPage.salesApp",
        value: 1840000,
        key: "app"
    },
    {
        titleKey: "pieChartPage.salesWeb",
        value: 1210000,
        key: "web"
    },
    {
        titleKey: "pieChartPage.salesMarket",
        key: "market",
        value: 780000
    },
    {
        titleKey: "pieChartPage.salesStore",
        value: 370000,
        key: "store"
    }
] as const;

const CITIES = [
    {
        titleKey: "pieChartPage.cityIstanbul",
        key: "istanbul",
        value: 4210
    },
    {
        titleKey: "pieChartPage.cityAnkara",
        key: "ankara",
        value: 1380
    },
    {
        titleKey: "pieChartPage.cityIzmir",
        key: "izmir",
        value: 1120
    },
    {
        titleKey: "pieChartPage.cityBursa",
        key: "bursa",
        value: 610
    },
    {
        titleKey: "pieChartPage.cityAntalya",
        key: "antalya",
        value: 540
    },
    {
        titleKey: "pieChartPage.cityKocaeli",
        key: "kocaeli",
        value: 390
    },
    {
        titleKey: "pieChartPage.cityKonya",
        key: "konya",
        value: 340
    },
    {
        titleKey: "pieChartPage.cityAdana",
        key: "adana",
        value: 310
    },
    {
        titleKey: "pieChartPage.cityGaziantep",
        key: "gaziantep",
        value: 280
    }
] as const;

const PAYMENTS = [
    {
        titleKey: "pieChartPage.payCredit",
        key: "credit"
    },
    {
        titleKey: "pieChartPage.payDebit",
        key: "debit"
    },
    {
        titleKey: "pieChartPage.payCash",
        key: "cash"
    },
    {
        titleKey: "pieChartPage.payTransfer",
        key: "transfer"
    },
    {
        titleKey: "pieChartPage.payWallet",
        key: "wallet"
    }
] as const;

const MONTHS = [
    {
        values: [
            1512,
            548,
            336,
            162,
            82
        ],
        key: "0"
    },
    {
        values: [
            1436,
            530,
            302,
            150,
            92
        ],
        key: "1"
    },
    {
        values: [
            1652,
            596,
            312,
            168,
            112
        ],
        key: "2"
    },
    {
        values: [
            1818,
            640,
            290,
            171,
            141
        ],
        key: "3"
    }
] as const;

const CURRENCIES = {
    tr: "TRY",
    en: "USD"
} as const;

const DEFAULT_MONTH = "3";

const SLICE_LIMIT = 6;

const getConfig = (items: ReadonlyArray<{
    colorIndex?: ChartConfigItem["colorIndex"];
    title: string;
    key: string;
}>): ChartConfig => Object.fromEntries(items.map((item, index) => [
    item.key,
    {
        colorIndex: item.colorIndex ?? (index + 1) as ChartConfigItem["colorIndex"],
        title: item.title
    }
]));

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("pieChartPage.activeTitle"),
        id: "active"
    },
    {
        title: t("pieChartPage.labelsTitle"),
        id: "labels"
    },
    {
        title: t("pieChartPage.legendTitle"),
        id: "legend"
    },
    {
        title: t("pieChartPage.otherTitle"),
        id: "other"
    },
    {
        title: t("pieChartPage.rangeTitle"),
        id: "range"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const PieChartPage = () => {
    const {
        language,
        t
    } = useDocsI18n();

    const {
        activeLocale
    } = NCoreUIKitLocalize.useContext();

    const {
        shapeConfigs
    } = NCoreUIKitTheme.useContext();

    const [
        activeKey,
        setActiveKey
    ] = useState<string | undefined>("android");

    const [
        month,
        setMonth
    ] = useState<string>(DEFAULT_MONTH);

    const getRows = (items: ReadonlyArray<{
        visitors?: number;
        value?: number;
        key: string;
    }>): Array<ChartDataRow> => items.map((item) => ({
        value: item.value ?? item.visitors ?? 0,
        name: item.key
    }));

    const getMonthTitle = (key: string, isLong: boolean) => formatDateTime({
        date: new Date(2026, Number(key), 1),
        locale: activeLocale,
        options: isLong ? {
            year: "numeric",
            month: "long"
        } : {
            month: "short"
        }
    });

    const formatMoney = (value: number) => formatCurrency({
        currency: CURRENCIES[language],
        options: {
            maximumFractionDigits: 1,
            notation: "compact"
        },
        locale: activeLocale,
        value
    });

    const monthValues = MONTHS.find((item) => item.key === month)?.values ?? MONTHS[MONTHS.length - 1]?.values ?? [];

    const monthRows: Array<ChartDataRow> = PAYMENTS.map((item, index) => ({
        value: monthValues[index] ?? 0,
        name: item.key
    }));

    const handleActiveChange = (key: string | undefined) => {
        if(key) {
            setActiveKey(key);
        }
    };

    const renderChart = (chart: ReactNode, props: {
        children?: ReactNode;
        config: ChartConfig;
        description: string;
        title: string;
        note?: string;
    }) => <View
        style={[
            stylesheet.chart,
            {
                maxWidth: shapeConfigs.contentMaxWidth * 2 / 3
            }
        ]}
    >
        <Chart
            description={props.description}
            spreadBehaviour="stretch"
            isAnimatedOnMount={true}
            config={props.config}
            title={props.title}
            note={props.note}
        >
            {props.children}
            {chart}
        </Chart>
    </View>;

    const activeDevice = DEVICES.find((item) => item.key === activeKey);

    const deviceTotal = DEVICES.reduce((total, item) => total + item.value, 0);

    const activeShare = activeDevice ? formatNumber({
        value: activeDevice.value / deviceTotal,
        options: {
            maximumFractionDigits: 1,
            style: "percent"
        },
        locale: activeLocale
    }) : undefined;

    return <Article>
        <ComponentHeader
            lead={t("pieChartPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("pieChartPage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("pieChartPage.overviewCaption")}
            >
                {
                    renderChart(<PieChart
                        centerTitle={t("pieChartPage.browserCenter")}
                        categoryTitle={t("pieChartPage.browser")}
                        data={getRows(BROWSERS)}
                        categoryKey="name"
                        valueKey="value"
                    />, {
                        description: t("pieChartPage.browserDescription"),
                        title: t("pieChartPage.browserTitle"),
                        config: getConfig(BROWSERS)
                    })
                }
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("pieChartPage.activeTitle")}
            id="active"
        >
            <Paragraph>
                {t("pieChartPage.activeText")}
            </Paragraph>
            <Preview
                caption={t("pieChartPage.activeCaption")}
            >
                {
                    renderChart(<PieChart
                        centerTitle={activeDevice ? t(activeDevice.titleKey) : undefined}
                        categoryTitle={t("pieChartPage.device")}
                        onActiveChange={handleActiveChange}
                        centerValue={activeShare}
                        data={getRows(DEVICES)}
                        activeKey={activeKey}
                        categoryKey="name"
                        valueKey="value"
                    />, {
                        description: t("pieChartPage.deviceDescription"),
                        config: getConfig(DEVICES.map((item) => ({
                            title: t(item.titleKey),
                            key: item.key
                        }))),
                        title: t("pieChartPage.deviceTitle")
                    })
                }
            </Preview>
            <CodeBlock
                code={"<PieChart\n    onActiveChange={setActiveKey}\n    activeKey={activeKey}\n    ...\n/>"}
                language="tsx"
            />
        </Section>
        <Section
            title={t("pieChartPage.labelsTitle")}
            id="labels"
        >
            <Paragraph>
                {t("pieChartPage.labelsText")}
            </Paragraph>
            <Preview
                caption={t("pieChartPage.labelsCaption")}
            >
                {
                    renderChart(<PieChart
                        categoryTitle={t("pieChartPage.ticketCategory")}
                        data={getRows(TICKETS)}
                        labelBehaviour="share"
                        isShowLegend={false}
                        categoryKey="name"
                        valueKey="value"
                        variant="pie"
                    />, {
                        description: t("pieChartPage.ticketDescription"),
                        config: getConfig(TICKETS.map((item) => ({
                            title: t(item.titleKey),
                            key: item.key
                        }))),
                        title: t("pieChartPage.ticketTitle")
                    })
                }
            </Preview>
        </Section>
        <Section
            title={t("pieChartPage.legendTitle")}
            id="legend"
        >
            <Paragraph>
                {t("pieChartPage.legendText")}
            </Paragraph>
            <Preview
                caption={t("pieChartPage.legendCaption")}
            >
                {
                    renderChart(<PieChart
                        categoryTitle={t("pieChartPage.salesChannel")}
                        centerTitle={t("pieChartPage.salesCenter")}
                        valueFormatter={formatMoney}
                        data={getRows(SALES)}
                        categoryKey="name"
                        valueKey="value"
                    />, {
                        description: t("pieChartPage.salesDescription"),
                        config: getConfig(SALES.map((item) => ({
                            title: t(item.titleKey),
                            key: item.key
                        }))),
                        title: t("pieChartPage.salesTitle")
                    })
                }
            </Preview>
        </Section>
        <Section
            title={t("pieChartPage.otherTitle")}
            id="other"
        >
            <Paragraph>
                {t("pieChartPage.otherText")}
            </Paragraph>
            <Preview
                caption={t("pieChartPage.otherCaption")}
            >
                {
                    renderChart(<PieChart
                        centerTitle={t("pieChartPage.cityCenter")}
                        categoryTitle={t("pieChartPage.city")}
                        maxSlices={SLICE_LIMIT}
                        data={getRows(CITIES)}
                        categoryKey="name"
                        valueKey="value"
                    />, {
                        description: t("pieChartPage.cityDescription"),
                        config: getConfig(CITIES.map((item) => ({
                            title: t(item.titleKey),
                            key: item.key
                        }))),
                        title: t("pieChartPage.cityTitle"),
                        note: t("pieChartPage.cityNote")
                    })
                }
            </Preview>
        </Section>
        <Section
            title={t("pieChartPage.rangeTitle")}
            id="range"
        >
            <Paragraph>
                {t("pieChartPage.rangeText")}
            </Paragraph>
            <Preview
                caption={t("pieChartPage.rangeCaption")}
            >
                {
                    renderChart(<PieChart
                        centerTitle={t("pieChartPage.paymentCenter")}
                        categoryTitle={t("pieChartPage.payment")}
                        categoryKey="name"
                        data={monthRows}
                        valueKey="value"
                    />, {
                        children: <Chart.Range
                            options={MONTHS.map((item) => ({
                                title: getMonthTitle(item.key, false),
                                key: item.key
                            }))}
                            onChange={setMonth}
                            value={month}
                        />,
                        config: getConfig(PAYMENTS.map((item) => ({
                            title: t(item.titleKey),
                            key: item.key
                        }))),
                        description: getMonthTitle(month, true),
                        title: t("pieChartPage.paymentTitle")
                    })
                }
            </Preview>
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("pieChartPage.a11yPlot"),
                    t("pieChartPage.a11yKeyboard"),
                    t("pieChartPage.a11yLegend"),
                    t("pieChartPage.a11yPattern"),
                    t("pieChartPage.a11yTable"),
                    t("pieChartPage.a11yMotion")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        type: "Array<Record<string, number | string>>",
                        description: t("pieChartPage.propData"),
                        name: "data"
                    },
                    {
                        description: t("pieChartPage.propCategoryKey"),
                        name: "categoryKey",
                        type: "string"
                    },
                    {
                        description: t("pieChartPage.propValueKey"),
                        name: "valueKey",
                        type: "string"
                    },
                    {
                        description: t("pieChartPage.propVariant"),
                        type: "\"pie\" | \"donut\"",
                        defaultValue: "\"donut\"",
                        name: "variant"
                    },
                    {
                        description: t("pieChartPage.propCenterTitle"),
                        name: "centerTitle",
                        type: "string"
                    },
                    {
                        description: t("pieChartPage.propCenterValue"),
                        name: "centerValue",
                        type: "string"
                    },
                    {
                        description: t("pieChartPage.propActiveKey"),
                        type: "string | null",
                        name: "activeKey"
                    },
                    {
                        description: t("pieChartPage.propOnActiveChange"),
                        type: "(key: string | undefined) => void",
                        name: "onActiveChange"
                    },
                    {
                        description: t("pieChartPage.propMaxSlices"),
                        defaultValue: "6",
                        name: "maxSlices",
                        type: "number"
                    },
                    {
                        description: t("pieChartPage.propOtherTitle"),
                        name: "otherTitle",
                        type: "string"
                    },
                    {
                        description: t("pieChartPage.propIsShowLegend"),
                        defaultValue: "true",
                        name: "isShowLegend",
                        type: "boolean"
                    },
                    {
                        description: t("pieChartPage.propLabelBehaviour"),
                        type: "\"none\" | \"share\"",
                        defaultValue: "\"none\"",
                        name: "labelBehaviour"
                    },
                    {
                        description: t("pieChartPage.propValueFormatter"),
                        type: "(value: number) => string",
                        name: "valueFormatter"
                    },
                    {
                        description: t("pieChartPage.propCategoryTitle"),
                        name: "categoryTitle",
                        type: "string"
                    },
                    {
                        description: t("pieChartPage.propPlotSize"),
                        type: "\"small\" | \"medium\" | \"large\"",
                        defaultValue: "\"small\"",
                        name: "plotSize"
                    },
                    {
                        description: t("pieChartPage.propSummary"),
                        name: "summary",
                        type: "string"
                    },
                    {
                        description: t("pieChartPage.propIsAnimatedOnMount"),
                        name: "isAnimatedOnMount",
                        type: "boolean"
                    },
                    {
                        description: t("pieChartPage.propTooltipBehaviour"),
                        type: "\"auto\" | \"follow\" | \"pinned\"",
                        name: "tooltipBehaviour"
                    },
                    {
                        type: "(content: ChartTooltipContent) => ReactNode",
                        description: t("pieChartPage.propRenderTooltip"),
                        name: "renderTooltip"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("pieChartPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("pieChartPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("pieChartPage.propsNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default PieChartPage;
